/**
 * What the platform calls the things it sells tickets to (D-378).
 *
 * The data model came from a theater, so its word is "performance": an event
 * (a title) has performances (dates). The first client is a ballpark, where
 * nobody says "performance" and "show" — a fan buys a ticket to a *game*, and
 * the gates open rather than the doors. The next client may be a theater
 * again, so the words are configuration (Admin → Branding → Terminology),
 * stored with the rest of the storefront's branding, never hard-coded.
 *
 * Two nouns, because two audiences:
 *
 *   - **The event term**, what customers read: storefront copy, emails, PDFs,
 *     wallet passes, and every error a customer can see. An event with an
 *     opponent (D-368) is always a "game"; any other event is the deployment's
 *     `event_one` ("event" unless a team says otherwise).
 *   - **The date term**, what staff call one dated occurrence (the model's
 *     performance): "Games" for the Bees, "Dates" for a generic venue.
 *
 * Customers never see "performance". The identifiers, API fields, columns and
 * routes keep the model's word; only what a person reads changes.
 */

/** The stored setting: `branding.terms`. Every field is a plain word or phrase. */
export interface TermsSetting {
  /** An event without an opponent: "event". */
  event_one: string;
  event_many: string;
  /** One dated occurrence, as staff say it: "date" (generic) or "game" (a ballpark). */
  date_one: string;
  date_many: string;
  /** When the venue lets people in: "Doors open" or "Gates open". */
  gates: string;
  /** When a game (an event with an opponent) starts: "Starts at", or "First pitch" at a ballpark. */
  game_starts: string;
}

/** A generic venue's words: what a deployment says until somebody changes them. */
export const DEFAULT_TERMS: TermsSetting = {
  event_one: 'event',
  event_many: 'events',
  date_one: 'date',
  date_many: 'dates',
  gates: 'Doors open',
  game_starts: 'Starts at',
};

/** The presets Admin offers. Choosing one fills the fields; each field can still be edited. */
export const TERMS_PRESETS = {
  generic: DEFAULT_TERMS,
  baseball: {
    event_one: 'event',
    event_many: 'events',
    date_one: 'game',
    date_many: 'games',
    gates: 'Gates open',
    game_starts: 'First pitch',
  },
} as const satisfies Record<string, TermsSetting>;

export type TermsPreset = keyof typeof TERMS_PRESETS;

export const TERMS_KEYS = ['event_one', 'event_many', 'date_one', 'date_many', 'gates', 'game_starts'] as const satisfies readonly (keyof TermsSetting)[];

export const MAX_TERM = 40;

/** Whatever is stored, coerced into a full setting: a blank or missing field is the default. */
export function parseTerms(raw: unknown): TermsSetting {
  const value = raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  const out = { ...DEFAULT_TERMS };
  for (const key of TERMS_KEYS) {
    const text = value[key];
    if (typeof text === 'string' && text.trim()) out[key] = text.trim().replace(/\s+/g, ' ').slice(0, MAX_TERM);
  }
  return out;
}

/** The preset a setting equals, or null when it has been edited. */
export function presetOf(setting: TermsSetting): TermsPreset | null {
  for (const [name, preset] of Object.entries(TERMS_PRESETS) as [TermsPreset, TermsSetting][]) {
    if (TERMS_KEYS.every((key) => preset[key] === setting[key])) return name;
  }
  return null;
}

/** The words for one context, ready to put in a sentence. */
export interface Terms {
  /** "game", "event", "date". */
  one: string;
  /** "games", "events", "dates". */
  many: string;
  /** "Game", "Event", "Date": the start of a sentence or a heading. */
  One: string;
  Many: string;
  /** "the game". */
  the: string;
  /** "a game", "an event". */
  a: string;
  /** "A game", "An event". */
  A: string;
  /** "First pitch" for a ballpark game; "Starts at" otherwise. */
  starts: string;
  /** "Gates open", "Doors open". */
  gates: string;
}

/** Anything that carries a setting: the branding record, the public config's branding, or the setting itself. */
export type TermsSource = { terms?: Partial<TermsSetting> | null } | Partial<TermsSetting> | null | undefined;

const settingOf = (source: TermsSource): TermsSetting => {
  if (!source) return DEFAULT_TERMS;
  if ('terms' in source) return parseTerms((source as { terms?: unknown }).terms);
  return parseTerms(source);
};

const capital = (word: string): string => word.charAt(0).toUpperCase() + word.slice(1);
/** "an" before a vowel sound as spelled; good enough for the nouns a venue uses. */
export const article = (word: string): string => (/^[aeiou]/i.test(word) ? 'an' : 'a');

const build = (one: string, many: string, starts: string, gates: string): Terms => ({
  one,
  many,
  One: capital(one),
  Many: capital(many),
  the: `the ${one}`,
  a: `${article(one)} ${one}`,
  A: capital(`${article(one)} ${one}`),
  starts,
  gates,
});

export interface TermsContext {
  /**
   * True: the event has an opponent, so it is a "game". False: it has none.
   * Left out: nothing is known (a cart of several, an error), so the event term.
   */
  opponent?: boolean | null | undefined;
  /** 'date' for the staff noun for one dated occurrence (the model's performance). */
  kind?: 'event' | 'date' | undefined;
}

/**
 * The words for a context (D-378).
 *
 *   termsFor(branding, { opponent: true })  → game / games / First pitch
 *   termsFor(branding, { opponent: false }) → event / events / Starts at
 *   termsFor(branding, { kind: 'date' })    → date / dates (game / games at a ballpark)
 */
export function termsFor(source: TermsSource, context: TermsContext = {}): Terms {
  const setting = settingOf(source);
  if (context.kind === 'date') return build(setting.date_one, setting.date_many, 'Starts at', setting.gates);
  if (context.opponent) return build('game', 'games', setting.game_starts, setting.gates);
  return build(setting.event_one, setting.event_many, 'Starts at', setting.gates);
}

/** The staff noun for one dated occurrence: `termsFor(source, { kind: 'date' })`. */
export const dateTermsFor = (source: TermsSource): Terms => termsFor(source, { kind: 'date' });

/**
 * Tokens a sentence can carry and have filled from the setting (D-378): the
 * API's error messages and the staff-editable email wording use them, so the
 * text is written once and says the deployment's words.
 *
 *   {event} {events} {Event} {Events}  the event term (no opponent known)
 *   {a_event} {A_event}                "an event", "An event"
 *   {date} {dates} {Date} {Dates}      the staff date term
 *   {a_date} {A_date}                  "a game", "a date"
 *   {gates}                            "Gates open" / "Doors open"
 */
export function fillTerms(text: string, source: TermsSource): string {
  if (!text.includes('{')) return text;
  const event = termsFor(source);
  const date = dateTermsFor(source);
  const values: Record<string, string> = {
    event: event.one,
    events: event.many,
    Event: event.One,
    Events: event.Many,
    date: date.one,
    dates: date.many,
    Date: date.One,
    Dates: date.Many,
    a_event: event.a,
    A_event: event.A,
    a_date: date.a,
    A_date: date.A,
    gates: event.gates,
  };
  return text.replace(TOKENS, (_, key: string) => values[key]!);
}

const TOKEN_NAMES = 'event|events|Event|Events|a_event|A_event|date|dates|Date|Dates|a_date|A_date|gates';
const TOKENS = new RegExp(`\\{(${TOKEN_NAMES})\\}`, 'g');

/** Whether a sentence carries any of `fillTerms`' tokens. */
export const hasTermTokens = (text: string): boolean => new RegExp(`\\{(${TOKEN_NAMES})\\}`).test(text);
